import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  CreditCard,
  Check,
  X,
  Zap,
  Star,
  ArrowRight,
  Rocket,
  Building2,
  Clock,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { openRazorpayCheckout } from '../../utils/razorpay';
import { formatError } from '../../utils/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPage() {
  const { agency, getAuthHeader, refreshAgency } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState({});
  const [boostPrice, setBoostPrice] = useState(0);
  const [boostPricing, setBoostPricing] = useState({ property_boosts: [], agency_boosts: [] });
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Boost purchase state
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedPropertyBoostDays, setSelectedPropertyBoostDays] = useState('');
  const [selectedAgencyBoostDays, setSelectedAgencyBoostDays] = useState('');
  const [boostProcessing, setBoostProcessing] = useState(false);

  useEffect(() => {
    fetchPlans();
    fetchProperties();
  }, []);

  const fetchPlans = async () => {
    try {
      const [plansRes, offersRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscription/plans`),
        axios.get(`${API_URL}/api/subscription/offers`)
      ]);
      setPlans(plansRes.data.plans);
      setBoostPrice(plansRes.data.boost_price);
      if (plansRes.data.boost_pricing) {
        setBoostPricing(plansRes.data.boost_pricing);
        // Auto-select first tier
        if (plansRes.data.boost_pricing.property_boosts?.length > 0) {
          setSelectedPropertyBoostDays(plansRes.data.boost_pricing.property_boosts[0].days.toString());
        }
        if (plansRes.data.boost_pricing.agency_boosts?.length > 0) {
          setSelectedAgencyBoostDays(plansRes.data.boost_pricing.agency_boosts[0].days.toString());
        }
      }
      setOffers(offersRes.data.offers);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/properties`, {
        headers: getAuthHeader()
      });
      const propList = res.data?.properties || res.data || [];
      setProperties(propList);
      if (propList.length > 0) {
        setSelectedPropertyId(propList[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  // Gumroad checkout URLs for each plan
  const GUMROAD_LINKS = {
    basic: 'https://rajeshcraft73.gumroad.com/l/jxdxtp',
    premium: 'https://rajeshcraft73.gumroad.com/l/acewud',
  };

  const subscribe = (planId) => {
    const gumroadUrl = GUMROAD_LINKS[planId];
    if (!gumroadUrl) {
      toast.error('No checkout link available for this plan');
      return;
    }
    // Pass agency email so Gumroad pre-fills it — needed for auto-activation
    const email = agency?.email || '';
    const url = email ? `${gumroadUrl}?email=${encodeURIComponent(email)}` : gumroadUrl;
    window.open(url, '_blank');
  };

  const downgrade = async (planId) => {
    const planName = planId.charAt(0).toUpperCase() + planId.slice(1);
    const confirmed = window.confirm(
      `Are you sure you want to downgrade to the ${planName} plan? You may lose access to premium features.`
    );
    if (!confirmed) return;

    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/payments/downgrade`, {
        plan_id: planId,
      }, {
        headers: getAuthHeader()
      });
      toast.success(`Successfully downgraded to ${planName} plan.`);
      if (refreshAgency) await refreshAgency();
      window.location.reload();
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || 'Failed to downgrade';
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  };

  const buyPropertyBoost = async () => {
    if (!selectedPropertyId) {
      toast.error('Please select a property to boost');
      return;
    }
    if (!selectedPropertyBoostDays) {
      toast.error('Please select a boost duration');
      return;
    }
    setBoostProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/boost/create-order`, {
        property_id: selectedPropertyId,
        origin_url: window.location.origin,
        boost_days: parseInt(selectedPropertyBoostDays)
      }, {
        headers: getAuthHeader()
      });

      const { order_id, amount, currency, key_id } = response.data;

      openRazorpayCheckout({
        key: key_id,
        order_id: order_id,
        amount: amount,
        currency: currency,
        name: 'EstateCloud',
        description: `Property Boost - ${selectedPropertyBoostDays} Days`,
        prefill_email: agency?.email,
        prefill_phone: agency?.phone,
        onSuccess: async (paymentData) => {
          try {
            await axios.post(`${API_URL}/api/payments/verify`, paymentData, {
              headers: getAuthHeader()
            });
            toast.success('Boost purchased! Your property will be highlighted.');
            if (refreshAgency) await refreshAgency();
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
          setBoostProcessing(false);
        },
        onError: (error) => {
          toast.error(error.error || 'Payment failed. Please try again.');
          setBoostProcessing(false);
        },
        onDismiss: () => {
          setBoostProcessing(false);
        }
      });
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || 'Failed to create boost order';
      toast.error(message);
      setBoostProcessing(false);
    }
  };

  const buyAgencyBoost = async () => {
    if (!selectedAgencyBoostDays) {
      toast.error('Please select a boost duration');
      return;
    }
    setBoostProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/agency-boost/create-order`, {
        origin_url: window.location.origin,
        boost_days: parseInt(selectedAgencyBoostDays)
      }, {
        headers: getAuthHeader()
      });

      const { order_id, amount, currency, key_id } = response.data;

      openRazorpayCheckout({
        key: key_id,
        order_id: order_id,
        amount: amount,
        currency: currency,
        name: 'EstateCloud',
        description: `Agency Boost - ${selectedAgencyBoostDays} Days`,
        prefill_email: agency?.email,
        prefill_phone: agency?.phone,
        onSuccess: async (paymentData) => {
          try {
            await axios.post(`${API_URL}/api/payments/verify`, paymentData, {
              headers: getAuthHeader()
            });
            toast.success('Agency boost purchased! All your properties will be highlighted.');
            if (refreshAgency) await refreshAgency();
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
          setBoostProcessing(false);
        },
        onError: (error) => {
          toast.error(error.error || 'Payment failed. Please try again.');
          setBoostProcessing(false);
        },
        onDismiss: () => {
          setBoostProcessing(false);
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create boost order');
      setBoostProcessing(false);
    }
  };

  const currentPlan = agency?.subscription || 'free';

  const getSelectedBoostPrice = (tiers, selectedDays) => {
    if (!tiers || !selectedDays) return null;
    const tier = tiers.find(t => t.days.toString() === selectedDays);
    return tier ? tier.price : null;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-80 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="subscription-page">
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Subscription Plans</h1>
        <p className="text-navy-600">Choose the plan that fits your business needs</p>
      </div>

      {/* Current Plan */}
      <Card className="mb-8 bg-navy-900 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-navy-300 text-sm">Your Current Plan</p>
              <p className="text-2xl font-bold capitalize">{currentPlan}</p>
              {agency?.subscription_expires && (
                <p className="text-sm text-navy-400 mt-1">
                  Expires: {new Date(agency.subscription_expires).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-navy-300 text-sm">Premium Boosts Remaining</p>
              <p className="text-3xl font-bold flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-400" />
                {agency?.premium_boosts_remaining || 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Offers */}
      {offers.length > 0 && (
        <div className="mb-8">
          {offers.map(offer => (
            <Card key={offer.id} className="bg-green-50 border-green-200">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-green-800">{offer.title}</p>
                  <p className="text-sm text-green-700">{offer.description}</p>
                </div>
                <Badge className="bg-green-500 text-lg px-4 py-1">
                  {offer.discount_percent}% OFF
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {Object.entries(plans).map(([key, plan]) => {
          const isCurrent = key === currentPlan;
          const isUpgrade = (currentPlan === 'free' && key !== 'free') ||
            (currentPlan === 'basic' && key === 'premium');
          const isDowngrade = (currentPlan === 'premium' && (key === 'basic' || key === 'free')) ||
            (currentPlan === 'basic' && key === 'free');

          return (
            <Card
              key={key}
              className={`relative ${key === 'premium' ? 'border-purple-500 border-2' : ''} ${isCurrent ? 'ring-2 ring-sky-500' : ''}`}
            >
              {key === 'premium' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-purple-500">Most Popular</Badge>
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 right-4">
                  <Badge className="bg-sky-500">Current Plan</Badge>
                </div>
              )}
              <CardHeader className="pt-8">
                <CardTitle className="text-xl flex justify-between items-start">
                  {plan.name}
                  {plan.discount_percent > 0 && (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
                      {plan.discount_percent}% OFF
                    </Badge>
                  )}
                </CardTitle>
                <div className="mt-2">
                  {plan.original_price && plan.original_price > plan.price ? (
                    <div>
                      <span className="text-sm text-navy-400 line-through">₹{plan.original_price}</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-navy-900">₹{plan.price}</span>
                        <span className="text-sm font-normal text-navy-500">/month</span>
                      </div>
                      <p className="text-xs text-green-600 font-medium mt-1">{plan.active_offer}</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold text-navy-900">
                      {plan.price === 0 ? 'Free' : `₹${plan.price}`}
                      {plan.price > 0 && <span className="text-sm font-normal text-navy-500">/month</span>}
                    </p>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    {plan.max_properties === -1 ? 'Unlimited' : plan.max_properties} properties
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    {plan.notifications ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-400" />
                    )}
                    Enquiry notifications
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Zap className="w-4 h-4 text-yellow-500" />
                    {plan.premium_boosts} premium boosts/month
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    {plan.analytics === 'full' ? 'Full analytics' : 'Basic analytics'}
                  </li>
                  {plan.featured_properties_limit != null && (
                    <li className="flex items-center gap-2 text-sm">
                      <Star className="w-4 h-4 text-amber-500" />
                      {plan.featured_properties_limit} featured {plan.featured_properties_limit === 1 ? 'property' : 'properties'}
                    </li>
                  )}
                </ul>

                {isCurrent ? (
                  <Button variant="outline" className="w-full" disabled>
                    Current Plan
                  </Button>
                ) : isUpgrade ? (
                  <Button
                    onClick={() => subscribe(key)}
                    disabled={processing}
                    className={`w-full gap-2 ${key === 'premium' ? 'bg-purple-500 hover:bg-purple-600' : 'bg-sky-500 hover:bg-sky-600'}`}
                    data-testid={`subscribe-${key}-btn`}
                  >
                    {processing ? 'Processing...' : 'Upgrade'}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : isDowngrade ? (
                  <Button
                    onClick={() => downgrade(key)}
                    disabled={processing}
                    variant="outline"
                    className="w-full gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid={`downgrade-${key}-btn`}
                  >
                    {processing ? 'Processing...' : 'Downgrade'}
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full" disabled>
                    Contact support
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ========== BOOST PURCHASE SECTION — commented out until payment gateway integration ==========
      <div className="mt-10">
        <h2 className="text-xl font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
          <Rocket className="w-6 h-6 text-amber-500" />
          Boost Your Properties
        </h2>
        <p className="text-navy-600 mb-6">
          Boosted properties appear prominently on the homepage and at the top of search results—get more views and enquiries.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-2 border-amber-200" data-testid="single-boost-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Single Property Boost
              </CardTitle>
              <p className="text-sm text-navy-500">Boost one specific property</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-navy-700">Select Property</label>
                <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a property..." />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-navy-700">Boost Duration</label>
                <div className="grid grid-cols-2 gap-2">
                  {(boostPricing.property_boosts || []).map(tier => (
                    <button
                      key={tier.days}
                      type="button"
                      onClick={() => setSelectedPropertyBoostDays(tier.days.toString())}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${selectedPropertyBoostDays === tier.days.toString()
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-navy-200 hover:border-amber-300'
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-navy-500" />
                        <span className="font-medium text-sm text-navy-900">{tier.label || `${tier.days} Days`}</span>
                      </div>
                      <p className="font-bold text-navy-900">₹{tier.price}</p>
                      <p className="text-xs text-navy-500">₹{Math.round(tier.price / tier.days)}/day</p>
                    </button>
                  ))}
                </div>
              </div>

              {selectedPropertyBoostDays && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="text-sm text-navy-600">Total</p>
                    <p className="text-xl font-bold text-navy-900">
                      ₹{getSelectedBoostPrice(boostPricing.property_boosts, selectedPropertyBoostDays)}
                    </p>
                  </div>
                  <Button
                    onClick={buyPropertyBoost}
                    disabled={boostProcessing || !selectedPropertyId}
                    className="bg-amber-500 hover:bg-amber-600 gap-2"
                    data-testid="buy-property-boost-btn"
                  >
                    {boostProcessing ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                    ) : (
                      <><Zap className="w-4 h-4" /> Buy Boost</>
                    )}
                  </Button>
                </div>
              )}

              {properties.length === 0 && (
                <p className="text-sm text-navy-500 italic">No properties yet. Create a property first to purchase boosts.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-200" data-testid="agency-boost-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-500" />
                Agency-Wide Boost
              </CardTitle>
              <p className="text-sm text-navy-500">Boost all your properties at once</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 mb-2">
                <div className="flex items-center gap-2 text-sm text-purple-700">
                  <TrendingUp className="w-4 h-4" />
                  All {properties.length} {properties.length === 1 ? 'property' : 'properties'} will be boosted
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-navy-700">Boost Duration</label>
                <div className="grid grid-cols-2 gap-2">
                  {(boostPricing.agency_boosts || []).map(tier => (
                    <button
                      key={tier.days}
                      type="button"
                      onClick={() => setSelectedAgencyBoostDays(tier.days.toString())}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${selectedAgencyBoostDays === tier.days.toString()
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-navy-200 hover:border-purple-300'
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-navy-500" />
                        <span className="font-medium text-sm text-navy-900">{tier.label || `${tier.days} Days`}</span>
                      </div>
                      <p className="font-bold text-navy-900">₹{tier.price}</p>
                      <p className="text-xs text-navy-500">₹{Math.round(tier.price / tier.days)}/day</p>
                    </button>
                  ))}
                </div>
              </div>

              {selectedAgencyBoostDays && (
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div>
                    <p className="text-sm text-navy-600">Total</p>
                    <p className="text-xl font-bold text-navy-900">
                      ₹{getSelectedBoostPrice(boostPricing.agency_boosts, selectedAgencyBoostDays)}
                    </p>
                  </div>
                  <Button
                    onClick={buyAgencyBoost}
                    disabled={boostProcessing}
                    className="bg-purple-500 hover:bg-purple-600 gap-2"
                    data-testid="buy-agency-boost-btn"
                  >
                    {boostProcessing ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                    ) : (
                      <><Building2 className="w-4 h-4" /> Boost All</>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      ========== END BOOST SECTION ========== */}
    </div>
  );
}
